/**
 * The customer's order in progress ("carrito"): the SKUs they added from the catalog (or loaded to
 * repeat a past order) before they review and send it. Prices are never stored here: the create
 * screen reads the current price of each SKU, and the server snapshots it when the order is placed.
 * Pure helpers only; `useOrderCart` keeps the result in the browser.
 */

export interface DraftItem {
  productId: string;
  skuId: string;
  /** Whole units, at least 1. */
  quantity: number;
}

/** Guard against a typo like 100000; a larger order is made by contacting sales. */
export const MAX_LINE_QUANTITY = 9999;

/** A whole number between 1 and MAX_LINE_QUANTITY; anything else becomes undefined. */
export function cleanQuantity(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return undefined;
  const whole = Math.trunc(n);
  return whole >= 1 ? Math.min(whole, MAX_LINE_QUANTITY) : undefined;
}

/** Add units of a SKU: adds to the existing line instead of repeating the SKU. */
export function addToDraft(items: readonly DraftItem[], add: DraftItem): DraftItem[] {
  const quantity = cleanQuantity(add.quantity);
  if (!quantity) return [...items];
  const existing = items.find((i) => i.skuId === add.skuId);
  if (!existing) return [...items, { ...add, quantity }];
  return items.map((i) =>
    i.skuId === add.skuId ? { ...i, quantity: Math.min(i.quantity + quantity, MAX_LINE_QUANTITY) } : i,
  );
}

/** Units of a SKU already in the order (0 when it is not). */
export function quantityInDraft(items: readonly DraftItem[], skuId: string): number {
  return items.find((i) => i.skuId === skuId)?.quantity ?? 0;
}

/** Read what was stored; anything malformed is dropped so a bad value never breaks the screen. */
export function parseDraft(raw: string | null): DraftItem[] {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const items: DraftItem[] = [];
    for (const row of data) {
      if (!row || typeof row !== 'object') continue;
      const { productId, skuId, quantity } = row as Record<string, unknown>;
      const q = cleanQuantity(quantity);
      if (typeof productId !== 'string' || typeof skuId !== 'string' || !productId || !skuId || !q) continue;
      if (items.some((i) => i.skuId === skuId)) continue;
      items.push({ productId, skuId, quantity: q });
    }
    return items;
  } catch {
    return [];
  }
}
